<template>
  <v-container fluid>
    <v-layout v-if="skusForDisplay.length > 0">SKUs:&nbsp;
      <span
        v-for="item in skusForDisplay" 
        :key="item"
      >
        <span :class="isSkuFulfilled(item.split(' ')[0]) ? '' : 'fulfillment_list'">{{item}}</span>
        <span>&nbsp;</span>
      </span>
    </v-layout>
    <v-layout v-if="selectProductWarehouse">
      {{ $t('selectedWarehouseSite') }}: 
      <div class="fulfillment_list">
        {{ selectProductWarehouse.siteName }}
      </div>
    </v-layout>
    <v-layout class="text-xs-center" justify-space-between align-center v-if="!isForecastedPkgs">
      <v-btn 
        flat 
        large 
        class="my-2 upload-file"
        color="primary" 
        @click.stop="showAddInventoriesDialog()" 
        :disabled="isBillingBalanceBelowCritical || isConfirmed"
      ><v-icon dark large class="mx-3">add</v-icon> {{ $t('addProduct') }}</v-btn>
      <v-alert
        :value="!!errorAlert"
        dismissible
        outline
        type="error">{{errorAlert}}</v-alert>
      <v-alert
        v-model="showAlert"
        dismissible
        outline
        type="error">{{errorMessage}}</v-alert>
      <v-flex xs5 lg4>
        <v-icon color="primary">info</v-icon>
        {{ $t('toEveryoneInfo') }}
      </v-flex>
      <v-flex xs3 lg2>
        <v-text-field
          append-icon="filter_list"
          :label="$t('search')"
          single-line
          hide-details
          v-model.trim="filter"
          clearable
        ></v-text-field>
      </v-flex>
    </v-layout>
    <v-form ref="form" v-model="isValid" lazy-validation>
      <v-data-table
        :headers="headers"
        :items="Object.values(this.products)"
        :search="filter"
        ref="dataTable"
        class="elevation-2"
        item-key="shipmentProductKey"
        :no-data-text="`--- ${$t('noProductSelected')} ---`"
        hide-actions
        v-if="!isForecastedPkgs"
      >
        <template v-slot:headerCell="props">
          <template v-if="props.header.value === 'toShip'">
            {{ $t('toShip') }} ({{ $t('total') }}: {{ filteredItems.reduce((acc, item) => (acc + (item.toShip || 0)), 0) }})
          </template>
          <template v-else-if="props.header.value === 'unitPrice'">
            {{ $t('unitPrice') }}({{ $t('total') }}: ${{ filteredItems.reduce((acc, item) => (acc + (item.toShip || 0) * (item.unitPrice || 0)), 0).toLocaleString() }})
          </template>
          <template v-else-if="props.header.value === 'unitCost'">
            {{ $t('unitCost') }}({{ $t('total') }}: ${{ filteredItems.reduce((acc, item) => (acc + (item.toShip || 0) * (item.unitCost || 0)), 0).toLocaleString() }})
          </template>
          <template v-else>
            {{ props.header.text }}
          </template>
        </template>
        <template v-slot:items="props">
          <td class="subheading">{{ props.index + 1 }}</td>
          <td>
            <ProductWidget
              :name="props.item.name"
              :condition="props.item.condition"
              :upc="props.item.upc"
              :sku="props.item.sku"></ProductWidget>
          </td>
          <td class="text-xs-left">{{ props.item.userName}}</td>
          <td class="text-xs-left">{{ props.item.siteName }}</td>
          <td class="text-xs-left">
            <v-flex md6>
              <v-text-field
                :class="selectedOrder && !isProductExhausted(props.item.fbmKey) ? 'fulfillment_warning_text' : ''"
                v-model.number="props.item.toShip"
                autofocus
                :rules="[fieldIsRequired('package quantity'), fieldIsOverZero('package quantity'), fieldIsInteger('package quantity')]"
                mask="#####"></v-text-field>   
            </v-flex>
          </td>
          <td class="text-xs-left">
            <v-flex md6>
              <v-text-field
                v-model.number="props.item.unitPrice"></v-text-field>   
            </v-flex>
          </td>
          <td class="text-xs-left">
            <v-flex md6>
              <v-text-field
                v-model.number="props.item.unitCost"></v-text-field>   
            </v-flex>
          </td>
          <td class="text-xs-left">{{ props.item.quantity }}</td>
          <td class="text-xs-center">
            <v-layout row justify-center>
              <v-flex><v-btn color="red" flat icon @click.stop="deleteProduct(props.item)" :disabled="isConfirmed"><v-icon>cancel</v-icon></v-btn></v-flex>
              <v-flex v-if="skus.size > 0">
                <v-menu offset-y>
                  <template v-slot:activator="{ on }">
                    <v-btn @click.stop="on.click" dark flat color="primary">
                      <v-icon>add</v-icon>Sku
                    </v-btn>
                  </template>
                  <v-list>
                    <v-list-tile v-for="item in skus.keys()" :key="item" @click="addSkuToProduct(props.item, item)">
                      <v-list-tile-title>{{item}}</v-list-tile-title>
                    </v-list-tile>
                  </v-list>
                </v-menu>
              </v-flex>
            </v-layout>
          </td>
        </template>
      </v-data-table>
      <v-data-table
      :headers="forecastTableHeader"
      :items="forecastedPackages"
      :search="filter"
      ref="dataTable"
      class="elevation-2"
      item-key="shipmentProductKey"
      :no-data-text="`--- ${$t('noProductSelected')} ---`"
      hide-actions
      v-if="isForecastedPkgs"
      >
      <template v-slot:items="props">
          <td class="text-xs-left">{{ props.item.forecastDocKey }}</td>
          <td class="text-xs-left">
            <div>
              {{
                isTenant
                  ? `${getWarehouseName(props.item.warehouseKey)}`
                  : `${getOrganizationName(props.item.organizationKey)}`
              }}
            </div>
            <div>
              
            </div>
          </td>
          <td class="text-xs-left">
            {{ getWarehouseSiteName(props.item.warehouseKey, props.item.warehouseSite) }}
          </td>
          <td class="text-xs-left">
            {{props.item.tracking}}
          </td>
          <td class="text-xs-left">
            <div v-for="(pkg, index) in props.item.upcs" :key="String(pkg.upc + index)">
              {{ pkg.upc }}
            </div>
          </td>
          <td class="text-xs-left">
            <div v-if="!isSplitShipment">
              <div v-for="(pkg, index) in props.item.upcs" :key="String(pkg.upc + index)">
                {{ (pkg.inboundQty || 0) - (pkg.outboundQty || 0)  }}
              </div>
            </div>
            <div v-else>
              <v-text-field
                :label="$t('quantity')"
                v-model.number="props.item.upcs[0].toShip"
                :rules="[fieldIsInteger('quantity'), (val)=> props.item.upcs[0].quantity >= val || 'Quantity exceeds' ]"
                class="required_field"
              ></v-text-field>
            </div>
          </td>
          <td class="text-xs-left" v-if="isSplitShipment">
            {{ props.item.upcs[0].quantity }}
          </td>
          <td class="text-xs-left">
            {{ toDateYYYYMMDD(props.item.warehousingTime) }}
          </td>
        </template>
      </v-data-table>
      <v-layout class="py-3 my-2 elevation-2">
        <v-flex xs6 md5>
          <v-layout align-center class="pa-0">
            <v-flex xs4 v-if="!this.isBlockUploadLabel" class="pr-2">
              <v-btn flat color="primary" class="upload-file" title="upload labels" :disabled="isConfirmed">
                <v-icon class="mr-1">cloud_upload</v-icon>
                {{ $t('upload') }}
                <input type="file" multiple v-on:click="clearSelectionCache" v-on:change="fileSelected" accept="*"/>
              </v-btn>
            </v-flex>
            
            <v-flex xs3 class="pr-2">
              <v-btn flat color="primary" class="upload-file" 
                :disabled="(!isForecastedPkgs && !Boolean(Object.keys(products).length)) || (!$store.getters.visibleBetaFeatures.includes('labelAndOrder') || isConfirmed || (selectedOrder && labels.length >= 1) || isMeasureDimension)" 
                @click="showCreateLabelDialog">
                <v-icon class="mr-1">shopping_cart</v-icon>
                {{ $t('buyLabel') }}
              </v-btn>
            </v-flex>
            
            <v-flex xs3 v-if="getIsMeasureDimensionOfWarehouse(forecastedPackages[0] || Object.values(this.products)[0]) && isTenant" class="pr-2">
              <v-tooltip top>
                <template v-slot:activator="tooltip">
                  <div v-on="tooltip.on">
                    <v-checkbox 
                      :disabled="!!labels.length" 
                      :label="$t('measureDimension')" 
                      v-model="isMeasureDimension"
                      class="ma-0 pa-0"
                      hide-details 
                    ></v-checkbox>
                  </div>
                </template>
                {{ $t('additionalServiceFeeMayApply') }}
              </v-tooltip>
            </v-flex>
            
            <v-flex xs3 v-if="isAllowMarkTechShipment && isTenant">
              <v-checkbox 
                :disabled="!!labels.length" 
                :label="$t('technical')" 
                v-model="isTechShipment"
                class="ma-0 pa-0"
                hide-details 
              ></v-checkbox>
            </v-flex>
          </v-layout>
        </v-flex>
        <v-flex xs6 md7>
          <v-layout align-center justify-end v-for="(item, index) in files" :key="item.file && item.file.name + index">
            <v-flex xs12 sm8 md5 class="text-lg-right text-md-right">
              {{item.file && item.file.name || item.name}}
            </v-flex>
            <v-flex md1 class="text-lg-center text-md-center"><v-icon>cloud_upload</v-icon></v-flex>
            <v-flex xs12 sm3 md1>
              <v-progress-circular
                :size="20"
                :width="3"
                :rotate="360"
                :value="item.task.progress"
                color="teal"
                class="ml-3"
                v-if="item.task && item.metadata && !item.metadata.downloadURL"
              >
              </v-progress-circular>
            </v-flex>
            <v-flex xs1>
              <v-tooltip top v-if="item.metadata && item.metadata.type === 'application/pdf'">
                <template v-slot:activator="tooltip">
                  <div v-on="tooltip.on">
                    <v-checkbox v-model="item.isMerge"></v-checkbox>
                  </div>
                </template>
                {{ $t(item.isMerge ? 'shippingLabel' : 'notShippingLabel') }}
              </v-tooltip>
            </v-flex>
            <v-flex xs12 sm3 md1 v-if="!isConfirmed">
              <v-btn dark small color="red" icon :disabled="isLoading" flat @click.stop="removeFileFromList(item, index)">
                <v-icon dark>cancel</v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
          <v-layout align-center justify-end v-for="item in labels || []" :key="item.labelKey">
            <v-flex xs12 sm8 md5 class="text-lg-right text-md-right">
              {{item.labelKey}}
            </v-flex>
            <v-flex md1 class="text-lg-center text-md-center"><v-icon>shopping_cart</v-icon></v-flex>
            <v-flex md1></v-flex>
            <v-flex xs1>
              <v-tooltip top>
                <template v-slot:activator="tooltip">
                  <div v-on="tooltip.on">
                    <v-checkbox v-model="item.isMerge"></v-checkbox>
                  </div>
                </template>
                {{ $t(item.isMerge ? 'shippingLabel' : 'notShippingLabel') }}
              </v-tooltip>
            </v-flex>
            <v-flex xs12 sm3 md1 v-if="!isConfirmed">
              <v-btn dark small color="red" icon flat @click.stop="removeLabelFromShipment(item)">
                <v-icon dark>cancel</v-icon>
              </v-btn>
            </v-flex>
          </v-layout>
        </v-flex>
      </v-layout>
      <v-layout justify-space-between row class="mt-3">
        <v-flex xs12 sm2>
          <v-text-field
            :label="$t('shipmentID')"
            v-model="shipmentId"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm2>
          <v-text-field
            :label="$t('trackingNumber')"
            v-model="trackingNum"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm2>
          <v-text-field
            :label="$t('packageQuantity')"
            v-model.number="packageQty"
            :rules="[
              !isMeasureDimension && fieldIsRequired('package quantity'),
              !isMeasureDimension && fieldIsOverZero('package quantity'), 
              fieldIsInteger('package quantity'),
              validQty,
            ]"
            class="required_field"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm2>
          <v-autocomplete
            :label="$t('serviceType')"
            v-model="isExpedited"
            item-text="label"
            item-value="value"
            :items="[{label: $t('normal'), value: false}, {label: $t('expedite'), value: true}]"></v-autocomplete>
        </v-flex>
        <v-flex xs12 sm2>
          <SelectWidget
            :disabled="Object.values(products) && !!Object.values(products).length && isHasDropshippingOrgByWarehouse(Object.values(this.products)[0].warehouseKey)"
            :label="$t('otherServices')"
            :items="otherServices"
            v-model="selectedOtherServices"
            itemText='displayName'
            itemValue='value'
            chipText='displayName'
            :mutuallyExclusives="['photo', 'photoForItem']"
            :displayQty="3"
            />
        </v-flex>
      </v-layout>
      <v-layout justify-space-between>
        <v-flex xs12 sm2>
          <v-autocomplete
            :label="$t('carrier')"
            v-model="carrier"
            :items="carriers"
            clearable></v-autocomplete>
        </v-flex>
        <v-flex xs12 sm2 v-if="carrier === $t('others')">                              
          <v-text-field
            :label="$t('carrier')"
            v-model="otherCarrier"
          ></v-text-field>
        </v-flex>
        <v-flex xs12 sm2 v-else></v-flex>
        <v-flex xs12 sm2>
          <v-autocomplete
            :label="$t('destination')"
            v-model="destination"
            :items="destinations"
            item-text="name"
            return-object
            clearable></v-autocomplete>
        </v-flex>
        <v-flex xs12 sm2  v-if="destination && destination.name === `-- ${$t('other')} --`">
          <v-text-field
            :label="$t('destinationContent')"
            v-model="destination.content"
          ></v-text-field>
        </v-flex>
        <v-flex sm2 v-else></v-flex>
        <v-flex sm2>
          <v-autocomplete
            style="margin-bottom: 0;"
            :label="$t('template')"
            v-model="instruction"
            :items="instructions"
            item-text="name"
            return-object
            clearable></v-autocomplete>
        </v-flex>
      </v-layout>
      <v-layout justify-space-between align-end>
        <v-flex xs5>
          <v-layout>
            <v-textarea
              :label="$t('noteToYourself')"
              v-model="note"
              outline
              rows="6"
              class="thinBox"
              persistent-hint
              :hint="$t('onlyVisibleToOrganization')"></v-textarea>
          </v-layout>
        </v-flex>
        <v-flex xs5>
          <v-layout>
            <v-textarea
              :label="$t('shipmentInstruction')"
              outline
              v-model="instructionContent"
              rows="6"
              class="thinBox"></v-textarea>
          </v-layout>
        </v-flex>
      </v-layout>
      <v-layout justify-space-between>
        <v-flex>
          <v-switch v-model="isAddIMEI" :label="$t('scanIMEI')"></v-switch>
          <template v-if="isAddIMEI">
            <span>
            {{ $t('currentPackageIndex')}}
            </span>
            <v-menu offset-y>
              <template v-slot:activator="{ on }">
                <v-btn @click.stop="on.click" dark small color="blue" style="font-size: 20px!important">
                {{currentIMEIPackage.index}}
              </v-btn>
              <span>{{ ' / ' + $t('totalQty') + ': ' + allIMEI.length}}</span>
              </template>
              <v-list>
                <v-list-tile v-for="(item, index) in IMEIRecords" :key="index" @click="currentIMEIPackage = IMEIRecords[index]">
                  <v-list-tile-title>{{item.index}}</v-list-tile-title>
                </v-list-tile>
              </v-list>
            </v-menu>
          </template>
        </v-flex>
        <v-flex>
          <slot name="leftBtn"/>
        </v-flex>
        <v-flex class="text-xs-right">
          <v-layout v-show="!isConfirmed" align-center>
            <v-flex>
              <p v-if="isBillingBalanceBelowCritical" class="warning_text">
                {{ $t('isBillingBalanceBelowCritical') }}
              </p>
            </v-flex>
            <v-flex xs4 v-if="selectedOrder">
              <v-text-field
                :label="$t('addSearchKeywords')"
                v-model="keywords"></v-text-field>   
            </v-flex>
            <LoaderButton
              v-if="!initDraft && !isForecastedPkgs"
              :buttonText="$t('createDraft')"
              :promiseAwait="createDraft"
              :disabled="isLoading"
              @input="(e) => isLoading = e"
            ></LoaderButton>
            <LoaderButton
              v-else-if="initDraft"
              :buttonText="$t('saveDraft')"
              :promiseAwait="saveDraft"
              :disabled="isLoading || isDraftDisabled"
              @input="(e) => isLoading = e"
            ></LoaderButton>
            <LoaderButton
              :buttonText="$t(initDraft ? 'publish' : 'createShipment')"
              :disabled="isBillingBalanceBelowCritical || (!isFileReady && !isMeasureDimension) || (Object.values(this.products).length === 0 && !isForecastedPkgs) || isConfirmed || !isValid || hasError || isLoading || isDraftDisabled"
              :promiseAwait="submitShipment"
              @input="(e) => isLoading = e"
            ></LoaderButton>
            <v-btn
              color="primary"
              @click="$router.push('/outBoundHistory')"
              :disabled="isLoading">{{ $t('status') }}</v-btn>
          </v-layout>
          <v-layout align-baseline  v-show="isConfirmed">
            <v-flex>{{ $t('success') }} !</v-flex>
            <v-btn
              color="primary"
              @click="reset">{{ $t('createAnother') }}</v-btn>
            <v-btn
              color="primary"
              @click="$router.push('/outBoundHistory')"
              :disabled="isLoading">{{ $t('status') }}</v-btn>
          </v-layout>
        </v-flex>
      </v-layout>

      <v-container v-if="isAddIMEI" fluid>
        <v-flex xs8>
          <v-text-field
          xs8
          md8
          id="inputBox"
          :label="$t('Barcode')"
          v-model="barcode"
          autofocus
          @keyup.enter="addBarcode">
        </v-text-field>

        </v-flex>

        <v-data-table :headers="IMEIPackagesHeader" :items="IMEIRecords" hide-actions item-key="index" caption>
            <template v-slot:headerCell="props">
              <span v-if="props.header.value === 'action'">
                <v-btn color="primary" flat small icon @click="addPackage()"
                  ><v-icon>add</v-icon></v-btn
                >
              </span>
              <span v-else>{{ props.header.text }}</span>
            </template>
            <template v-slot:items="props">
              <tr @click="currentIMEIPackage = IMEIRecords[props.index]">
                <td class="text-xs-center">
                <v-btn
                  v-if="IMEIRecords.length > 1"
                  color="primary"
                  flat
                  small
                  icon
                  @click="deletePackage(props.index)"
                  ><v-icon>delete</v-icon>
                </v-btn>
              </td>
              <td class="text-xs-center">{{ props.item.index }}</td>
              <td class="text-xs-center">{{ getPackageTotalQty(props.item) }}</td>
              <td class="text-xs-center">
                <v-layout
                  v-for="upc of Object.keys(props.item.products)" 
                  justify-start row :key="upc" 
                  align-center
                  mb-1
                  style=" overflow-wrap: break-word; word-break: normal;" wrap>
                  <v-chip close  @click="editIMEI(props.item, upc)" @input="deleteUpc(props.index, upc)"> 
                    {{ upc }}
                    <v-flex ml-1 class="text-xs-left" v-if="props.item.products[upc] && props.item.products[upc].length">
                    {{(`(${props.item.products[upc].slice(-6).join(', ')}${props.item.products[upc].length > 6 ? '...' : ''})`) }}
                  </v-flex></v-chip>
                </v-layout>
              </td>
              </tr>
            </template>
          </v-data-table>
      </v-container>
    </v-form>
    <AddInventories
      :title="$t('selectProduct')"
      v-model="addInventoriesDialog"
      v-if="addInventoriesDialog"
      @deleteDistribution="(item) => deleteProduct(item)"
      @addDistribution="(item) => addProduct(item)"
      :addedDistributionSet="addedDistributionSet"
      :editMode="true"
      :skus="skus"
      :selectProductWarehouse="selectProductWarehouse"
      :allDistribution="allDistributionWithCombinedUpcs">
    </AddInventories>
    <LabelCreatePopup
      v-model="createLabelDialog" 
      v-if="createLabelDialog"
      :froms="addresses"
      executeOutside
      :idGenerator="labelIdGenerator"
      @submitted="addToLabels"
      :labelInfo="labelInfo"
      :initTo="shipTo"
      :onlyOneLabel="onlyOneLabel"
      :labelNote="labelNote"
      :order="order"
      :products="products"
      :forecastedPackages="forecastedPackages"
      :initTemplate="template"/>
    <IMEIArrayEditPopup
      v-model="IMEIEditDialog"
      v-if="IMEIEditDialog"
      :isIMEI="true"
      :snArray="currentUpcIMEIs"
      :upc="activeUpc"
      :snItems="items"
      :allItems="allIMEI"
      @submitted="IMEIChanged"/>
  </v-container>
</template>

<script>
import ProductWidget from './ProductWidget'
import AddInventories from './AddInventories'
import LoaderButton from './LoaderButton'
import SelectWidget from './SelectWidget'
import { sortNoCase, checkRules, getRandomIdByTime, timeTools, validateIMEI, mediaTools } from '../utils/tools'
import * as rules from '../utils/rules'
import firebase from 'firebase/compat/app'
import LabelCreatePopup from './LabelCreatePopup'
import BarcodeFinder from '../utils/barcode'
import SnArrayEditPopup from './SnArrayEditPopup'

export default {
  name: 'toEveryone',
  components: {
    ProductWidget,
    AddInventories,
    LoaderButton,
    SelectWidget,
    LabelCreatePopup,
    IMEIArrayEditPopup: SnArrayEditPopup
  },

  mixins: [checkRules, timeTools, mediaTools],
  data () {
    const {
      recipientName,
      shipAddress1,
      shipAddress2,
      shipCity,
      shipPostalCode,
      shipState,
      buyerPhoneNumber
    } = this.selectedOrder || {}
    return {
      products: {},
      files: [],
      addInventoriesDialog: false,
      productInEdit: {},
      user: {},
      carrier: '',
      otherCarrier: '',
      selectedOtherServices: [],
      otherServices: [
        {value: 'label', displayName: this.$t('UPCLabel')}, 
        {value: 'photo', displayName: this.$t('photoForSN')}, 
        {value: 'photoForItem', displayName: this.$t('photoForItem')}, 
        {value: 'IMEI', displayName: this.$t('IMEI')}, 
        {value: 'SN', displayName: this.$t('sn')}
      ],
      carriers: ['USPS', 'FedEx', 'UPS', 'DHL', this.$t('others')],
      instruction: {},
      instructionContent: '',
      note: '',
      shipmentId: '',
      trackingNum: '',
      destination: {},
      isConfirmed: false,
      packageQty: 0,
      isValid: true,
      isExpedited: false,
      createLabelDialog: false,
      labels: [],
      requestId: getRandomIdByTime(3),
      labelInfo: {},
      shipTo: this.selectedOrder ? {
        fullName: recipientName,
        address1: shipAddress1,
        address2: shipAddress2,
        city: shipCity,
        zipCode: shipPostalCode.slice(0, 5),
        state: shipState,
        phone: buyerPhoneNumber
      } : {},
      labelIndex: 0,
      keywords: '',
      isLoading: false,
      filter: '',
      filteredItems: [],
      isDraftDisabled: false,
      isInitCanceledShipment: false,
      forecastedPackages: [],
      isMeasureDimension: false,
      isAddIMEI: false,
      IMEIRecords: [],
      currentIMEIPackage: {index: 1},
      activeUpc: '',
      upcInEdit: {},
      IMEIEditDialog: false,
      barcode: '',
      soundPitch: 1,
      isBlockUploadLabel: false,
      isAllowMarkTechShipment: false,
      selectProductWarehouse: null,
      errorAlert: null
    }
  },
  beforeRouteEnter (to, from, next) {
    if (to.path === '/toEveryone/initDraft' && from.path === '/signin') {
      next('/toEveryone')
    } else {
      next()
    }
  },
  beforeRouteLeave (to, from, next) {
    // todo: add data diff check
    if (!this.isConfirmed && !this.isDraftDisabled) {
      const answer = window.confirm(this.$t('doYouReallyWantToLeave?UnsavedChangesWillBeMissing.'))
      if (answer) {
        next()
      } else {
        next(false)
      }
    } else {
      next()
    }
  },
  mounted () {
    this.selectedPackages.length && this.otherServices.push({value: 'packageReinforcement', displayName: this.$t('packageReinforcement')})
    this.selectedPackages.length > 1 && this.otherServices.push({value: 'packageConsolidation', displayName: this.$t('PackageConsolidation')})
    this.isSplitShipment && this.otherServices.push({value: 'unpacking', displayName: this.$t('unpacking')}) 
    this.forecastedPackages = this.selectedPackages
    if (this.isSplitShipment) {
      this.forecastedPackages = this.selectedPackages.reduce((pkgs, pkg) => {
        return [...pkgs, ...pkg.upcs.reduce((upcs, upcItem, index) => {
          const upc = pkg.upcs[index]
          const { inboundQty = 0, outboundQty = 0 } = upc || {}
          const stock = inboundQty - outboundQty
          if (stock <= 0) return upcs
          upcs.push({
            ...pkg,
            upcs: [{...upc, quantity: inboundQty - outboundQty, toShip: 0}]
          })
          return upcs
        }, [])]
      }, [])
    }
    this.$watch(() => this.$refs.dataTable.filteredItems, (newValue, oldValue) => { this.filteredItems = newValue })
    if (this.initDraft) {
      this.initThisFromDraft()
    }
    // auto add distribution
    if (this.distribution && this.selectedOrder && this.selectedOrder.items.length === 1) {
      let fitDistributions = this.distribution
        .filter(item => item.sku && item.sku
          .some(sku => sku === this.selectedOrder.items[0].sku))
          
      if (fitDistributions.length === 1) {
        let {warehouseSite, id} = fitDistributions[0]
        const {quantityPurchased, quantityShipped} = this.selectedOrder.items[0]
        this.$set(this.products, id, {
          ...fitDistributions[0], 
          toShip: quantityPurchased - quantityShipped,
          shipmentProductKey: `${warehouseSite}${id}`,
          fbmKey: id
        })
      }
    }

    if (this.template) {
      const {selectedOtherServices, isExpedited, instruction} = this.template
      Object.assign(this, {selectedOtherServices, isExpedited, instructionContent: instruction})
    } else {
      this.autoAddOthersServices()
    }
    this.soundPitch = Number(window.localStorage.getItem('inbound.soundPitch' + this.uid) || 1)
    let cache = window.localStorage.getItem('IMEIRecords')
    if (cache && cache.length) {
      this.IMEIRecords = JSON.parse(cache)
      this.currentIMEIPackage = this.IMEIRecords[this.IMEIRecords.length - 1]
    }
  },
  computed: {
    sumToShip () {
      return Object.values(this.products).reduce((sum, item) => {
        sum += item.toShip
        return sum
      }, 0)
    },
    isLockWarehouse () {
      return !!this.labels.length
    },
    allDistributionWithCombinedUpcs () {
      const allDistributionWithCombinedUpcs = [...this.allDistribution, ...this.combinedUpcs]
      return allDistributionWithCombinedUpcs
    },
    combinedUpcs () {
      return this.$store.getters.combinedUpcs.map(item => {
        return {
          ...item,
          quantity: item.totalQuantity
        }
      }) || []
    },
    firstProductWarehouseKey () {
      const firstProduct = Object.values(this.products)[0]
      return firstProduct ? firstProduct.warehouseKey : null
    },
    forecastTableHeader () {
      return this.isSplitShipment ? [
        { text: this.$t('id#'), align: 'left', sortable: false },
        { text: this.$t('name'), align: 'left', sortable: false, width: '10%' },
        { text: this.$t('site'), align: 'left', sortable: false, width: '10%' },
        { text: this.$t('tracking'), value: 'tracking', align: 'left', sortable: false },
        { text: `${this.$t('upc')} / sku`, align: 'left', sortable: false },
        { text: `${this.$t('toShip')}`, align: 'left', sortable: false },
        { text: this.$t('available'), value: 'quantity', align: 'left', sortable: false },
        { text: `Warehousing time`, align: 'left', sortable: false }
      ] : [
        { text: this.$t('id#'), align: 'left', sortable: false },
        { text: this.$t('name'), align: 'left', sortable: false, width: '10%' },
        { text: this.$t('site'), align: 'left', sortable: false, width: '10%' },
        { text: this.$t('tracking'), value: 'tracking', align: 'left', sortable: false },
        { text: `${this.$t('upc')} / sku`, align: 'left', sortable: false },
        { text: `${this.$t('toShip')}`, align: 'left', sortable: false },
        { text: `Warehousing time`, align: 'left', sortable: false }
      ]
    },
    IMEIPackagesHeader () {
      return [
        { text: 'Action', value: 'action', align: 'center', sortable: false, width: '5%' },
        { text: this.$t('packageIndex'), value: 'index', align: 'center', sortable: false, width: '5%' },
        { text: this.$t('quantity'), value: 'quantity', align: 'center', sortable: false, width: '10%' },
        { text: this.$t('upc/(imei)'), value: 'upcs', align: 'center', sortable: false, width: '70%' }
      ]
    },
    upcToProductMap () {
      return this.$store.getters.upcMap
    },
    currentUpcIMEIs () {
      return (this.currentIMEIPackage.products && this.currentIMEIPackage.products[this.activeUpc]) || []
    },
    allIMEI () {
      return this.IMEIRecords.reduce((sum, pkg) => {
        return [...sum, ...Object.values(pkg.products).flat()]
      }, [])
    },
    warehouses () { 
      return this.$store.getters.warehouses || []
    },
    isTenant () {
      return !!this.$store.getters.activeOrganization
    },
    isForecastedPkgs () {
      return this.selectedPackages.length > 0
    },
    organizations () {
      return this.$store.getters.sortedWarehouseOrganizations?.filter(element => {
        return element.organizationId !== ''
      }) || []
    },
    skus () {
      let map = new Map()
      if (!this.selectedOrder) return map
      let {items} = this.selectedOrder
      items.forEach(item => {
        let {sku, quantityPurchased, quantityShipped} = item
        if (!map.has(sku)) {
          map.set(sku, 0)
        }
        map.set(sku, map.get(sku) + quantityPurchased - quantityShipped)
      })
      return map
    },
    isProductsOutOfSkus () {
      if (!this.skus.size) return false
      return Object.values(this.products).some(product => {
        return !product.sku ||
          product.sku.every(sku => {
            return !this.skus.has(sku)
          })
      })
    },
    fulfillment () { 
      let fulfillment = new Map()
      let surplusProducts = new Map()
      
      Object.keys(this.products).forEach(fbmKey => {
        let {toShip, sku} = this.products[fbmKey]
        surplusProducts.set(fbmKey, {toShip, sku})
      })

      this.skus.forEach((value, key) => {
        fulfillment.set(key, 0)
      })

      surplusProducts.forEach((product, fbmKey) => {
        let {sku = []} = product
        sku.forEach(matchingSku => {
          if (fulfillment.has(matchingSku) && product.toShip > 0) {
            let matchingQty = Math.min(product.toShip, this.skus.get(matchingSku) - fulfillment.get(matchingSku))
            product.toShip -= matchingQty
            fulfillment.set(matchingSku, fulfillment.get(matchingSku) + matchingQty)
          }
        })
      })
      return {fulfillment, surplusProducts}
    },
    // with the assumption that there would not be an sku assigned to more than one upcs
    skusForDisplay () {
      return [...this.skus.entries()]
        .map(entry => `${entry[0]} (${entry[1]})`)
    },
    members () {
      let members = Object.values(this.products).map(product => { 
        return {
          userName: product.userName, 
          userKey: product.uid, 
          warehouseKey: product.warehouseKey || ''
        } 
      })
      return members
    },
    isFileReady () {
      return this.files.length + this.labels.length > 0 && this.files.every(item => { return item.task && item.task.progress === 100 && item.metadata && !!item.metadata.downloadURL })
    },
    isAnyToshipZero () {
      return Object.values(this.products).length === 0 || Object.values(this.products).some(item => {
        return !item.toShip || item.toShip === 0
      })
    },
    hasProductEmptyUpc () {
      return Object.values(this.products).some(product => !product.upc)
    },
    destinations () {
      return [...[...this.$store.getters.tenant.destinations].sort(sortNoCase('name')), {name: `-- ${this.$t('other')} --`, content: ''}] || [{name: `-- ${this.$t('other')} --`, content: ''}]
    },
    instructions () {
      return this.$store.getters.tenant.instructions || []
    },
    isBillingBalanceBelowCritical () {
      let selectedItems = this.isForecastedPkgs ? this.forecastedPackages : Object.values(this.products)
      let selectedWarehouseKeys = new Set(
        selectedItems
          .filter(item => item.warehouseKey)
          .map(item => item.warehouseKey)
      )
      let warehouses = this.$store.getters.warehousesWithBalanceAndThreshold
        .filter(warehouse => warehouse.warehouseName !== 'Others' && selectedWarehouseKeys.has(warehouse.warehouseKey))
      return warehouses.some(warehouse => warehouse.balance && parseFloat(warehouse.balance) <= (warehouse.lowestBalance || warehouse.criticalLowBalanceThreshold || 0))
    },
    isAllProductsOneSite () {
      let productsSet = new Set(Object.values(this.products).map(product => product.warehouseSite))
      return productsSet.size < 2
    },
    allDistribution () {
      return this.$store.getters.allDistribution
    },
    addedDistributionSet () {
      return new Set(Object.keys(this.products || {}))
    },
    addresses () {
      return this.$store.getters.tenantAddresses
    },
    hasError () {
      return this.isBillingBalanceBelowCritical ||  
        this.hasProductEmptyUpc || 
        !this.isAllProductsOneSite || 
        this.isProductsOutOfSku
    },
    showAlert: {
      get: function () {
        return this.hasError
      },
      set: function (val) {
        
      }
    },
    errorMessage () {
      if (this.isBillingBalanceBelowCritical) return this.$t('isBillingBalanceBelowCritical')
      if (this.hasProductEmptyUpc) return this.$t('hasProductEmptyUpc')
      if (!this.isAllProductsOneSite) return this.$t('isAllProductsOneSite')
      if (this.isProductsOutOfSkus) return this.$t('isProductsOutOfSkus')
    },
    headers () {
      return [
        { text: this.$t('id#'), value: 'onlyForSearch', align: 'left', sortable: false, width: '5%' },
        { text: this.$t('product'), value: 'name', align: 'left', sortable: false },
        { text: this.$t('user'), value: 'userName', align: 'left', sortable: false },
        { text: this.$t('site'), value: 'siteName', align: 'left', sortable: false },
        { 
          text: this.$t('toShip'), 
          value: 'toShip', 
          align: 'left', 
          sortable: false 
        },
        { text: this.$t('unitPrice'), value: 'unitPrice', align: 'left', sortable: false },
        { text: this.$t('unitCost'), value: 'unitCost', align: 'left', sortable: false },
        { text: this.$t('available'), value: 'quantity', align: 'left', sortable: false },
        { text: this.$t('actions'), value: 'upc', align: 'center', sortable: false, width: '8%' }
      ]
    },
    routeQueryData () {
      return this.$route.query
    },
    isDefaultSnService () {
      return (this.$store.getters.tenantLimitedInfo.defaultSelectedOtherServices || []).includes('SN')
    },
    isDefaultLabelService () {
      return (this.$store.getters.tenantLimitedInfo.defaultSelectedOtherServices || []).includes('label')
    },
    isDefaultPhotoService () {
      return (this.$store.getters.tenantLimitedInfo.defaultSelectedOtherServices || []).includes('photo')
    },
    isDefaultPhotoForItemService () {
      return (this.$store.getters.tenantLimitedInfo.defaultSelectedOtherServices || []).includes('photoForItem')
    },
    tenantProducts () {
      return this.$store.getters.products || []
    }
  },
  watch: {
    products (newVal) {
      const values = Object.values(newVal)
      if (values.length) {
        this.selectProductWarehouse = values[0]
      } else if (!this.isLockWarehouse) {
        this.selectProductWarehouse = null
      }
    },
    isBlockUploadLabel (newVal, oldVal) {
      if (!oldVal && newVal) {
        this.files = []
      }
    },
    isAllowMarkTechShipment (newVal, oldVal) {
      if (!oldVal && newVal) {
        this.isTechShipment = false
      }
    },
    firstProductWarehouseKey (newVal, oldVal) {
      if (newVal && newVal !== oldVal) {
        this.$store.dispatch('getOrgForWarehouse', {warehouseKey: newVal}).then(res => {
          const {isBlockUploadLabel, isAllowMarkTechShipment} = res || {}
          this.isBlockUploadLabel = isBlockUploadLabel || false
          this.isAllowMarkTechShipment = isAllowMarkTechShipment || false
        })
      }
    },
    members (value) {
      this.user = this.members[0] || {}
    },
    instruction (value) {
      if (value) {
        this.instructionContent = this.instruction.content || ''
      } else {
        this.instructionContent = ''
      }
    },
    destination (value) {
      if (!value) {
        this.destination = {}
      }
    }, 
    isAddIMEI (value) {
      if (value && !this.IMEIRecords.length) {
        this.IMEIRecords.push({products: {}, index: 1})
        this.currentIMEIPackage = this.IMEIRecords[0]
      }
    }
  },
  methods: {
    validQty (v) {
      const qty = this.isForecastedPkgs 
        ? !this.isSplitShipment 
          ? this.forecastedPackages.length 
          : this.forecastedPackages.reduce((sum, item) => {
            sum += item.upcs[0].quantity
            return sum
          }, 0)
        : this.sumToShip
      return v <= qty || `can't not great than ${qty}` 
    },
    isHasDropshippingOrgByWarehouse (warehouseKey) {
      const {featureInboundForecastDropshipping = []} = this.$store.getters.tenant || {}
      return this.isTenant && featureInboundForecastDropshipping.includes(warehouseKey)
    },
    getIsMeasureDimensionOfWarehouse (item) {
      if (!item) return false
      return this.$store.getters.warehouseLimitedInfoForTenant?.some(warehouse => {
        return warehouse.key === item.warehouseKey && warehouse.featuresInOrg?.includes('measureDimension')
      }) 
    },
    getWarehouseSiteName (warehouseKey, warehouseSite) {
      return this.isTenant ?
        (this.warehouses.find(warehouse => warehouseKey === warehouse.warehouseKey) || {})
          .sites.find?.(site => site.key === warehouseSite)?.siteName || '' : 
        (this.warehousesSites.find(site => site.key === warehouseSite) || {}).siteName || ''
    },
    getWarehouseName (warehouseKey) {
      return (this.warehouses.find(warehouse => warehouseKey === warehouse.warehouseKey) || {}).warehouseName
    },
    getOrganizationName (organizationKey) {
      return (this.organizations.find(organization => organizationKey === organization.key) || {}).tenantName
    },
    initThisFromDraft () {
      Object.entries(this.initDraft).forEach(([key, val]) => {
        if (this[key] !== undefined) {
          switch (key) {
            case 'otherServices':
              this.$set(this, 'selectedOtherServices', val)
              break
            case 'instruction' :
              typeof val === 'string' && this.$set(this, key, {content: val})
              break
            case 'isMeasureDimension':
              this.getIsMeasureDimensionOfWarehouse(this.initDraft.products[0]) && this.$set(this, key, val)
              break
            default:
              this.$set(this, key, val)
              break
          }
        }
      })

      Object.entries(this.products).forEach(([key, val]) => {
        let target = this.allDistribution.find(item => item.fbmKey === key) || {}
        this.products[key].quantity = target.quantity || 0
      })
    },
    reset () {
      if (this.initDraft) {
        this.$router.push('/toEveryone')
        return
      }
      this.products = {}
      this.files = []
      this.labels = []
      this.addInventoriesDialog = false
      this.productInEdit = {}
      this.user = {}
      this.carrier = ''
      this.otherCarrier = ''
      this.instruction = {}
      this.instructionContent = ''
      this.note = ''
      this.trackingNum = ''
      this.isConfirmed = false
      this.destination = {}
      this.isExpedited = false
      this.packageQty = 0
      this.shipmentId = ''
      this.selectedOtherServices = []
      this.otherServices = this.otherServices.map(item => {
        const {disabled, ...rest} = item
        return rest
      })
      this.requestId = getRandomIdByTime(3)
      this.isLoading = false
      this.IMEIRecords = []
      this.selectProductWarehouse = null
    },
    quantityChanged (event, item) {
      if (rules.isNumber(event) === true) {
        if (event > item.quantity) {
          alert(`input exceed available quantity of ${item.quantity}`)
          item.toShip = item.quantity
        }
      } else {
        alert('quantity format invalid')
      }
    },
    clearSelectionCache (e) {
      e.target.value = null
    },
    fileSelected (e) {
      Object.values(e.target.files).forEach(newFile => {
        if (this.checkFilenameExists(newFile && newFile.name)) {
          this.$store.dispatch('showToast', {info: this.$t('fileExistsNewFileName', { name: newFile.name })})
          console.log('file already selected: ', newFile.name)
          return
        }
        newFile.prettySize = newFile.size > 1048576 ?
          (Math.round(newFile.size / 1048576) + ' ' + 'MB') 
          :
          newFile.size > 1024 ? 
            (Math.round(newFile.size / 1024) + ' ' + 'KB')
            :
            newFile.size + ' ' + 'B'
        if (newFile.size > 5242880) {
          this.$store.dispatch('showToast', {info: this.$t('uploadFileTooBig', { name: newFile.name, fileSizeLimit: '5' }), level: 'error'})
          return 
        }
        this.$store.dispatch('uploadLabels', newFile)
          .then(rtn => {
            console.log('uploaded File: ', rtn.metadata.name)
            this.listenToFileUpload(rtn)
            this.files.push({...rtn, isMerge: true})
          })
      })      
    },
    checkFilenameExists (filename) {
      return this.files.findIndex(item => item.metadata && item.metadata.name === filename) !== -1
    },
    listenToFileUpload (file) {
      // Listen for state changes, errors, and completion of the upload.
      file.task.on(firebase.storage.TaskEvent.STATE_CHANGED, // or 'state_changed'
        (snapshot) => {
          // Get task progress, including the number of bytes uploaded and the total number of bytes to be uploaded
          var progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          this.$set(file.task, 'progress', progress)
        }, (error) => {
          // A full list of error codes is available at
          // https://firebase.google.com/docs/storage/web/handle-errors
          switch (error.code) {
            case 'storage/unauthorized':
              // User doesn't have permission to access the object
              break

            case 'storage/canceled':
              // User canceled the upload
              break

            case 'storage/unknown':
              // Unknown error occurred, inspect error.serverResponse
              break
          }
        }, () => {
          // Upload completed successfully, now we can get the download URL
          file.task.snapshot.ref.getDownloadURL().then(downloadURL => {
            this.$set(file.metadata, 'downloadURL', downloadURL)
            console.log('File available at', downloadURL)
          })
        })
    },
    // deleteFileFromStorage (file) {
    //   return this.$store.dispatch('removeFile', file.fullPath || file.file.fullPath || file.metadata.fullPath)
    //     .catch(error => {
    //       console.error('error removing file: ', error)
    //     })
    // },
    removeFileFromList (file, index, noPrompt) {
      if (noPrompt || confirm(this.$t('uploadFileRemove'))) {
        // file.task && file.task.cancel && file.task.cancel()
        // if ((file.task && file.task.snapshot.state === 'success') || file.downloadURL) {
        //   this.deleteFileFromStorage(file)
        //     .catch(error => {
        //       if (error.code === 'storage/object-not-found') {
        //         this.files.splice(index, 1)
        //       } else {
        //         this.$store.dispatch('showToast', {info: this.$t('uploadFileDeleteFailed')})
        //       }
        //     })
        //     .then(rtn => {
        //       this.files.splice(index, 1)
        //     })
        // } else {
        this.files.splice(index, 1)
        // }
      }
    },
    showAddInventoriesDialog () {
      this.addInventoriesDialog = true
    },
    showAddProductDialog (product) {
      this.productInEdit = product
      this.addProductDialog = true
    },
    addProduct (distribution) {
      this.$set(this.products, distribution.fbmKey, {
        ...distribution, 
        toShip: 0, 
        unitPrice: 0,
        unitCost: 0,
        shipmentProductKey: `${distribution.warehouseSite}${distribution.id}`
      })
    },
    handlerAddShipments (promise) {
      return promise
        .then(() => {
          if (this.initDraft && this.initDraft.requestId) {
            return this.$store.dispatch('cancelShipmentDraft', this.initDraft)
          }
        })
        .then(() => {
          console.log('create shipment successful')
          this.isConfirmed = true
          if (this.selectedPackages.length) {
            let pkgs = Object.values(this.forecastedPackages.reduce((pkgs, pkg) => {
              if (!pkgs[pkg.tracking]) pkgs[pkg.tracking] = pkg
              else pkgs[pkg.tracking].upcs.push(pkg.upcs[0])
              return pkgs
            }, {}))
            this.$emit('submitted', pkgs)
          } else {
            this.$emit('submitted', true)
          }
        })
        .catch(error => {
          console.error(error)
          this.isConfirmed = false
          if (error?.message === 'draft-not-existed') {
            this.isDraftDisabled = true
            alert(this.$t('draftNotExisted'))
          } else if (error?.message === 'shipment-existed') {
            this.$store.dispatch('showToast', {info: this.$t('shipmentExisted')})
          } else this.$store.dispatch('showToast', {info: this.$t('createShipmentFailed')})
        })
    },
    deleteProduct (distribution) {
      this.$delete(this.products, distribution.fbmKey)
    },
    async submitShipment () {
      this.validSkus()
      if (this.isBillingBalanceBelowCritical) return Promise.reject(Error('very low balance'))
      if (!this.$refs.form.validate()) return Promise.reject(Error('Form validate failed.'))
      let isMeasureDimensionOfWarehouse = this.getIsMeasureDimensionOfWarehouse(this.forecastedPackages[0] || Object.values(this.products)[0])
      const files = this.files.map((file) => {
        const {metadata} = file
        if (!metadata) return {...file}
        return {...file.metadata, isMerge: !!file.isMerge}
      })
      let products = Object.values(this.products).map(product => {
        let {
          id, 
          name, 
          condition, 
          siteName, 
          toShip, 
          uid, 
          upc, 
          userName, 
          warehouseSite, 
          fbmKey, 
          asin = [], 
          unitPrice = 0,
          unitCost = 0,
          warehouseKey = '',
          isCombined = false,
          components = []
        } = product
        return {
          id,
          name,
          condition: condition || 'new',
          siteName, 
          toShip,
          uid,
          upc,
          userName,
          warehouseSite,
          fbmKey,
          asin,
          unitPrice,
          unitCost,
          warehouseKey,
          isCombined: isCombined,
          components
        }
      })
      if (this.isForecastedPkgs) {
        let payload = {
          _key: this.requestId,
          products: this.forecastedPackages.reduce((products, pkg) => {
            let {tracking, warehouseSite, warehouseKey, organizationKey, forecastDocKey, upcs = []} = pkg
            let product = {
              upc: this.isSplitShipment ? `${tracking}-${upcs[0].upc}` : tracking,
              isPackageManagement: true,
              tracking,
              warehouseSite,
              warehouseKey,
              organizationKey,
              toShip: this.isSplitShipment ? upcs[0].toShip : 1,
              forecastDocKey
            }
            if (!product.toShip) return products
            products.push(product)
            return products
          }, []),
          files,
          warehouseKey: this.forecastedPackages[0].warehouseKey,
          userKey: this.forecastedPackages[0].warehouseKey,
          userName: this.getWarehouseName(this.forecastedPackages[0].warehouseKey),
          status: 'created',
          packageQty: this.packageQty,
          shipmentId: this.shipmentId,
          trackingNum: this.trackingNum,
          note: this.note,
          instruction: this.instructionContent,
          destination: this.destination || {},
          carrier: this.carrier === 'others' ? this.otherCarrier : (this.carrier || ''),
          isExpedited: this.isExpedited,
          otherServices: this.selectedOtherServices,
          siteKey: products[0]?.warehouseSite || '',
          labels: this.labels,
          isForecastedPackages: true,
          isForecastedSplitShipment: this.isSplitShipment,
          isMeasureDimension: this.isMeasureDimension,
          isTechShipment: (this.isTechShipment && this.isAllowMarkTechShipment) || false
        }
        if (this.IMEIRecords.length) {
          payload.IMEIRecords = this.IMEIRecords
        }
        if (!this.isMeasureDimension && this.initDraft) {
          payload.otherServices = payload.otherServices.filter(item => item !== 'measureDimension')
        }
        isMeasureDimensionOfWarehouse && this.isMeasureDimension && !payload.otherServices.includes('measureDimension') && payload.otherServices.push('measureDimension')
        this.selectedPackages.length > 1 && !payload.otherServices.includes('packageConsolidation') && payload.otherServices.push('packageConsolidation')
        this.isSplitShipment && !payload.otherServices.includes('unpacking') && payload.otherServices.push('unpacking')
        if (!payload.products.length) return Promise.reject(Error('No valid quantity'))
        return this.handlerAddShipments(this.$store.dispatch('createForecastShipment', payload))
      }
      let payload = {
        _key: this.requestId,
        products,
        files,
        ...this.user,
        status: 'created',
        packageQty: this.packageQty,
        shipmentId: this.shipmentId,
        trackingNum: this.trackingNum,
        note: this.note,
        instruction: this.instructionContent,
        destination: this.destination || {},
        carrier: this.carrier === 'others' ? this.otherCarrier : (this.carrier || ''),
        isExpedited: this.isExpedited,
        otherServices: this.selectedOtherServices,
        siteKey: products[0].warehouseSite,
        labels: this.labels,
        isMeasureDimension: this.isMeasureDimension,
        isTechShipment: this.isTechShipment
      }
      if (this.IMEIRecords.length) {
        payload.IMEIRecords = this.IMEIRecords
      }
      if (!this.isMeasureDimension && this.initDraft) {
        payload.otherServices = payload.otherServices.filter(item => item !== 'measureDimension')
      }
      isMeasureDimensionOfWarehouse && this.isMeasureDimension && !payload.otherServices.includes('measureDimension') && payload.otherServices.push('measureDimension')
      if (this.selectedOrder) {
        payload.selectedOrder = this.selectedOrder
        payload.selectedProducts = Object.values(this.products)
        payload.keywords = this.keywords + ' shipping'
      }
      if (this.initDraft && !this.isInitCanceledShipment) {
        payload.draftKey = this.initDraft._key
      }
      payload.isDropshippingPackage = this.isHasDropshippingOrgByWarehouse(payload.warehouseKey)
      if (payload.isDropshippingPackage) {
        payload.otherServices = []
        payload.billingWeight = 0
      }
      localStorage.removeItem('IMEIRecords')
      return this.handlerAddShipments(this.$store.dispatch('addShipment', payload))        
    },
    getBillingBalance () {
      let warehouses = this.$store.getters.warehousesWithBalanceAndThreshold
      let criticalLowBalanceThreshold = warehouses
        .reduce(
          (maxLowBalanceThreshold, curWarehouse) => 
            maxLowBalanceThreshold >= (curWarehouse.criticalLowBalanceThreshold || 0) 
              ? maxLowBalanceThreshold : curWarehouse.criticalLowBalanceThreshold, 
          (warehouses[0].criticalLowBalanceThreshold || 0)
        )
      this.isBillingBalanceNegative = warehouses.some(warehouse => warehouse.balance && parseFloat(warehouse.balance) <= criticalLowBalanceThreshold)
    },
    isSkuFulfilled (sku) {
      let {fulfillment} = this.fulfillment
      return fulfillment.get(sku) === this.skus.get(sku)
    },
    isProductExhausted (fbmKey) {
      let {surplusProducts} = this.fulfillment
      return surplusProducts.get(fbmKey).toShip === 0
    },
    async showCreateLabelDialog (item) {
      if (this.isForecastedPkgs) {
        const {address1, address2, city, state, zipCode, phone} = this.$store.getters.siteKeyMap.get(this.forecastedPackages[0].warehouseSite)
        this.labelInfo.from = {address1, address2, city, state, zipCode, phone}
      } else if (Object.values(this.products)[0]) {
        await this.getShipFrom(Object.values(this.products)[0])
      }
      this.createLabelDialog = true
    },
    addToLabels (labels) {
      this.labels = [...this.labels, ...labels.map(item => {
        return {...item, isMerge: true}
      })]
    },
    labelIdGenerator (qty) {
      let rtn = []
      for (let i = 0; i < qty; i++) {
        if (this.labelIndex > 99) throw Error('Exceeded label limit(100) from this shipment.')
        if (this.labelIndex < 10) {
          rtn.push(`${this.requestId}A0${this.labelIndex}`)
        } else {
          rtn.push(`${this.requestId}A${this.labelIndex}`)
        }
        this.labelIndex++
      }
      return rtn
    },
    removeLabelFromShipment (label) {
      if (!confirm(this.$t('confirmRemoveLabel'))) return 
      this.labels = this.labels.filter(item => item.orderId !== label.orderId)
      if (!this.labels.length && !Object.values(this.products).length) this.selectProductWarehouse = null
    },
    async addSkuToProduct (product, sku) {
      try {
        const isSkuExist = this.tenantProducts.some(p => {
          const isCurrentProduct = p.id === product.id
          if (isCurrentProduct) return false
          return Array.isArray(p.sku) && p.sku.includes(sku)
        })

        if (isSkuExist) {
          this.$store.dispatch('showToast', {level: 'error', info: this.$t('skuAlreadyExists')})
          return
        }

        const hasSkuInCombos = this.$store.getters.combinedUpcs.some(combo => 
          combo.sku && combo.sku.includes(sku)
        )

        if (hasSkuInCombos) {
          this.$store.dispatch('showToast', {level: 'error', info: this.$t('skuExistsInCombination')})
          return
        }

        let originSku = Array.isArray(product.sku) ? product.sku : [product.sku].filter(Boolean)
        let newSkus = [...new Set([...originSku, sku])]

        if (product.isCombinedUpc) {
          await this.$store.dispatch('updateCombinedUpc', {
            id: product.id,
            sku: newSkus
          })
        } else {
          const newProduct = {
            ...product,
            sku: newSkus
          }
          delete newProduct.onlyForSearch
          await this.$store.dispatch('editProduct', { newProduct })
        }
        this.$set(this.products[product.fbmKey], 'sku', newSkus)
        this.$store.dispatch('showToast', {level: 'success', info: this.$t('skuAddedSuccessfully')})
      } catch (error) {
        this.$store.dispatch('showToast', {level: 'error', info: error.message || this.$t('failedToUpdateProduct')})
      }
    },
    async getShipFrom (distribution) {
      const initFrom = await this.$store.dispatch('getShipFrom', distribution)
      this.labelInfo.from = initFrom
    },
    extractDraftData () {
      const {
        addInventoriesDialog, 
        otherServices, 
        carriers,       
        isConfirmed,
        isValid, 
        createLabelDialog, 
        isLoading,
        headers,
        isInitCanceledShipment,
        audioCtx,
        AudioContext,
        ...rest
      } = this.$data
      return rest
    },
    validSkus () {
      this.errorAlert = null
      const skuEntries = [...this.skus.entries()]
      const skuQtyMap = Object.values(this.products).reduce((obj, item) => {
        const { sku = [], toShip } = item
        sku.forEach(row => {
          if (!obj[row]) obj[row] = toShip
          else obj[row] += toShip
        })
        return obj
      }, {})
      const invalidSku = skuEntries.find(([sku, qty]) => {
        return skuQtyMap[sku] > qty
      })
      if (invalidSku) {
        const [sku, qty] = invalidSku
        const message = `The number of sku ${sku} cannot exceed ${qty} `
        this.errorAlert = message
        throw Error(message)
      }
      return invalidSku
    },
    createDraft () {
      this.validSkus()
      localStorage.removeItem('IMEIRecords')
      return this.$store.dispatch('createShipmentDraft', this.extractDraftData())
        .finally(() => {
          this.isConfirmed = true
        })
    },
    async saveDraft () {
      this.validSkus()
      localStorage.removeItem('IMEIRecords')
      return !this.routeQueryData.isDraft ? this.createDraft() 
        : this.$store.dispatch('updateShipmentDraft', {data: this.extractDraftData(), key: this.initDraft._key})
          .finally(() => {
            this.isConfirmed = true
          })
    },
    autoAddOthersServices () {
      if (this.initDraft) return
      this.isDefaultSnService && !this.selectedOtherServices.includes('SN') && this.selectedOtherServices.push('SN')
      this.isDefaultLabelService && !this.selectedOtherServices.includes('label') && this.selectedOtherServices.push('label')
      this.isDefaultPhotoService && !this.selectedOtherServices.includes('photo') && this.selectedOtherServices.push('photo')
      this.isDefaultPhotoForItemService && !this.selectedOtherServices.includes('photoForItem') && this.selectedOtherServices.push('photoForItem')
    },
    addBarcode (event) {
      if (!Object.keys(this.currentIMEIPackage).length) {
        this.currentIMEIPackage = this.IMEIRecords[0]
      }
      if (!this.barcode) return
      this.barcode = this.barcode.replace(/[^\w-]/g, '')
      if (this.activeUpc && !this.checkUpcValid(this.barcode)) {
        this.addIMEI(event)
      } else {
        this.addUpc()
      } 
      this.playSound('success')
    },
    checkUpcValid (barcode) {
      const { ignoredCheckUpcs = [] } = this.generalSettings || {}
      return BarcodeFinder.isUpcValid(barcode) || ignoredCheckUpcs.includes(barcode)
    },
    playSound (type) {
      if (type === 'hint') return this.hintSound(this.soundPitch)()
      if (type === 'success') return this.successSound(this.soundPitch)()
      if (type === 'warning') return this.warningSound(this.soundPitch)()
      return this.closeSound()
    },
    IMEIChanged (items) {
      this.currentIMEIPackage.products[this.activeUpc] = items
    },
    addUpc (event) {
      if (!this.checkUpcValid(this.barcode)) {
        this.$store.dispatch('showToast', {info: this.$t('addTrackingError')})
        event && event.target.select()
        this.playSound('warning')
        return
      }
      this.activeUpc = this.barcode
      if (!this.currentIMEIPackage.products[this.activeUpc]) {
        this.$set(this.currentIMEIPackage.products, this.activeUpc, [])
      }
      window.localStorage.setItem('IMEIRecords', JSON.stringify(this.IMEIRecords))
      this.barcode = ''
    },
    addIMEI (event) {
      const value = this.barcode.trim()
      if (!validateIMEI(value)) {
        this.$store.dispatch('showToast', {info: this.$t('invalidIMEI')})
        event && event.target.select()
        this.playSound('warning')
        return
      }
      let isDup = this.allIMEI.includes(this.barcode)
      if (isDup) {
        this.$store.dispatch('showToast', {level: 'error', info: this.$t('duplicateIMEI')})
        event && event.target.select()
        this.playSound('warning')
        return
      }
      this.currentIMEIPackage.products[this.activeUpc].push(this.barcode)
      window.localStorage.setItem('IMEIRecords', JSON.stringify(this.IMEIRecords))
      this.barcode = ''
    },
    deleteUpc (index, upc) {
      if (confirm(this.$t('confirmAction'))) {
        this.$delete(this.IMEIRecords[index].products, upc) 
      }
    },
    deletePackage (index) {
      if (confirm(this.$t('confirmAction'))) {
        this.IMEIRecords.splice(index, 1)
      }
      this.currentIMEIPackage = this.IMEIRecords[this.IMEIRecords.length - 1]
    },
    editIMEI (item, upc) {
      this.activeUpc = upc
      this.currentIMEIPackage = item
      this.IMEIEditDialog = true
    },
    getPackageTotalQty (item) {
      return Object.values(item.products).reduce((sum, item) => { return item.length + sum }, 0)
    },
    addPackage () {
      this.IMEIRecords.push({products: {}, index: this.IMEIRecords[this.IMEIRecords.length - 1].index + 1})
      this.currentIMEIPackage = this.IMEIRecords[this.IMEIRecords.length - 1]
    }
  },
  props: {
    initDraft: Object,
    selectedOrder: Object,
    template: Object,
    onlyOneLabel: Boolean,
    labelNote: String,
    order: Object,
    selectedPackages: {
      type: Array,
      default: () => []
    },
    isSplitShipment: {
      default: false,
      type: Boolean
    }
  }
}
</script>

<style scoped>
.upload-file input[type=file] {
  position: absolute;
  top: 0;
  right: 0;
  min-width: 100%;
  min-height: 100%;
  text-align: right;
  filter: alpha(opacity=0);
  opacity: 0;
  outline: none;
  cursor: inherit;
  display: block;
}
</style>

<style>
.edit_text {
  border: 1px solid rgba(0,0,0,.54);
}

.warning_text {
  color:  red;
}

.fulfillment_warning_text input[type="text"] {
  background-color : yellow
}

.fulfillment_list {
  background-color : yellow
}
</style>



// WEBPACK FOOTER //
// src/components/ToEveryone.vue